import { useEffect, useState } from "react";

import dayjs from "dayjs";
import { Controller, useForm } from "react-hook-form";

import { fetchPlaceOptions } from "@/apis/place";
import {
  fetchMyVehicleReservations,
  getReservationByUsers,
  putReservationState,
  updateVehicleReservationStatus,
} from "@/apis/reservation";
import Button from "@/components/Button";
import Dropdown from "@/components/Dropdown/Dropdown";
import AlertModal from "@/components/Modal/AlertModal";
import ConfirmModal from "@/components/Modal/ConfirmModal";
import Pagination from "@/components/Pagination";
import Spinner from "@/components/Spinner";
import Table from "@/components/Table";
import useModal from "@/hooks/useModal";
import TableFilterButton from "@/pages/Admin/components/TableFilterButton";
import MyPageWrapper from "@/pages/MyPage/components/MyPageLayout";
import DatePickerButton from "@/pages/Reservation/components/DropdownDatePicker";
import useLoginStore from "@/store/loginStore";
import { ReservationByState, ReservationStateCode } from "@/types/reservation";
import { CellInfo } from "@/types/table";
import type { VehicleReservation } from "@/types/vehicleReservation";
import { VEHICLE_STATUS_LABEL } from "@/types/vehicleReservation";
import { cn } from "@/utils/cn";
import {
  getMyReservationBodyData,
  getTableBody,
  getTableHeader,
} from "@/utils/table";

const reservationTableHeader = ["날짜", "시간", "장소", "사용목적", "상태", ""];
// 컬럼 폭 고정 (페이지 변경 시 폭이 변하지 않도록)
const reservationTableWidths = ["20%", "18%", "22%", "18%", "12%", "10%"];
const header = getTableHeader(reservationTableHeader, reservationTableWidths);

const vehicleTableHeader = [
  "날짜",
  "운행시간",
  "차량",
  "목적지",
  "사용목적",
  "상태",
  "",
];
const vehicleTableWidths = ["15%", "16%", "16%", "15%", "16%", "12%", "10%"];
const vehicleHeader = getTableHeader(vehicleTableHeader, vehicleTableWidths);

const filterButtons = [
  { name: "전체", type: "all" },
  { name: "승인 대기", type: "request" },
  { name: "승인 완료", type: "approve" },
  { name: "승인 반려", type: "reject" },
  { name: "예약 취소", type: "cancel" },
];

const PAGE_SIZE = 10;

const MyReservation = () => {
  const { tokenId } = useLoginStore();
  const [activeTab, setActiveTab] = useState<"place" | "vehicle">("place");

  // 장소 예약
  const [reservationBody, setReservationBody] = useState<CellInfo[][]>([]);
  const [selectedReservation, setSelectedReservation] =
    useState<ReservationByState | null>(null);
  const [reservations, setReservations] = useState([]);
  const [page, setPage] = useState(0);
  const [pageCount, setPageCount] = useState(0);
  const [selectedSttCd, setSelectedSttCd] = useState<string>("all");
  const [isPlaceLoading, setPlaceLoading] = useState(true);
  const [isVehicleLoading, setVehicleLoading] = useState(true);

  // 필터 폼
  const { control, getValues } = useForm({
    defaultValues: {
      plcCd: { id: "all", name: "전체" },
      fromDate: dayjs().subtract(30, "day"),
      toDate: dayjs().add(30, "day"),
    },
  });
  const [placeList, setPlaceList] = useState<any[]>([]);

  // 차량 예약
  const [vehicleReservations, setVehicleReservations] = useState<
    VehicleReservation[]
  >([]);
  const [vehicleBody, setVehicleBody] = useState<CellInfo[][]>([]);
  const [selectedVehicle, setSelectedVehicle] =
    useState<VehicleReservation | null>(null);

  const [alertMessage, setAlertMessage] = useState("");
  const confirmModal = useModal();
  const alertModal = useModal();

  // 장소 목록 로드
  useEffect(() => {
    const load = async () => {
      setPlaceList(await fetchPlaceOptions("all", { includeAll: true }));
    };
    load();
  }, []);

  // 장소 예약 로드
  const getMyReservation = async () => {
    const plcCd = getValues("plcCd").id;
    const fromDate = getValues("fromDate").format("YYYYMMDD");
    const toDate = getValues("toDate").format("YYYYMMDD");

    setPlaceLoading(true);
    try {
      const result = await getReservationByUsers(page, PAGE_SIZE, tokenId, {
        sttCd: selectedSttCd,
        plcCd,
        fromDate,
        toDate,
      });
      if (result) {
        setReservations(result.content || []);
        setPageCount(result.totalPages || 0);
      }
    } finally {
      setPlaceLoading(false);
    }
  };

  // 차량 예약 로드
  const getMyVehicleReservation = async () => {
    setVehicleLoading(true);
    try {
      const data = await fetchMyVehicleReservations(tokenId);
      setVehicleReservations(data);
    } finally {
      setVehicleLoading(false);
    }
  };

  useEffect(() => {
    getMyReservation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSttCd, page]);

  useEffect(() => {
    getMyVehicleReservation();
  }, []);

  // 장소 예약 테이블 바디
  useEffect(() => {
    const reservationTableBody = reservations.map(reservation => {
      const { sttCd } = reservation;
      const cancelReservation = () => {
        setSelectedReservation(reservation);
        setSelectedVehicle(null);
        confirmModal.onOpen();
      };

      return getMyReservationBodyData(reservation, sttCd, cancelReservation);
    });
    setReservationBody(
      getTableBody(reservationTableHeader, reservationTableBody),
    );
  }, [reservations]);

  // 차량 예약 테이블 바디
  useEffect(() => {
    const body: CellInfo[][] = vehicleReservations.map(v => {
      const canCancel = v.status === "pending" || v.status === "approved";
      const cancelVehicle = () => {
        setSelectedVehicle(v);
        setSelectedReservation(null);
        confirmModal.onOpen();
      };

      return vehicleTableHeader.map(h => {
        switch (h) {
          case "날짜":
            return { name: h, data: v.reservationDate };
          case "운행시간":
            return { name: h, data: v.drivingTime };
          case "차량":
            return { name: h, data: v.vehicleName || "-" };
          case "목적지":
            return { name: h, data: v.destination };
          case "사용목적":
            return { name: h, data: v.purpose };
          case "상태":
            return { name: h, data: VEHICLE_STATUS_LABEL[v.status] };
          default:
            return {
              name: "",
              data: canCancel ? "취소" : "",
              method: canCancel ? cancelVehicle : undefined,
            };
        }
      });
    });
    setVehicleBody(body);
  }, [vehicleReservations]);

  const changeReservationState = async (
    rsvtId: number,
    sttCd: ReservationStateCode,
  ) => {
    await putReservationState({ rsvtId, sttCd });
    getMyReservation();
  };

  const [actionLoading, setActionLoading] = useState(false);

  const onConfirm = async () => {
    if (actionLoading) return;
    setActionLoading(true);
    try {
      if (selectedReservation) {
        await changeReservationState(selectedReservation.rsvtId, "cancel");
        setAlertMessage("예약이 취소되었습니다.");
      } else if (selectedVehicle) {
        await updateVehicleReservationStatus(selectedVehicle.id, "cancelled");
        setAlertMessage("차량 예약이 취소되었습니다.");
        getMyVehicleReservation();
      } else {
        setAlertMessage("요청을 처리할 수 없습니다. 다시 시도해 주세요.");
      }
    } finally {
      setActionLoading(false);
    }
    alertModal.onOpen();
    confirmModal.onClose();
  };

  const handleSearch = () => {
    setPage(0);
    getMyReservation();
  };

  const handleFilterClick = (type: string) => {
    setSelectedSttCd(type);
    setPage(0);
  };

  return (
    <MyPageWrapper>
      <div className="flex w-full flex-col p-4">
        {/* 탭 */}
        <div className="mb-4 flex gap-2">
          <button
            type="button"
            className={cn(
              "rounded-full border px-4 py-1.5 text-sm font-medium transition-colors",
              activeTab === "place"
                ? "border-primary bg-primary text-white"
                : "border-gray-200 bg-white text-gray-600 hover:border-primary/50 hover:text-primary",
            )}
            onClick={() => setActiveTab("place")}
          >
            장소 예약
          </button>
          <button
            type="button"
            className={cn(
              "rounded-full border px-4 py-1.5 text-sm font-medium transition-colors",
              activeTab === "vehicle"
                ? "border-primary bg-primary text-white"
                : "border-gray-200 bg-white text-gray-600 hover:border-primary/50 hover:text-primary",
            )}
            onClick={() => setActiveTab("vehicle")}
          >
            차량 예약
          </button>
        </div>

        {activeTab === "place" && (
          <>
            {/* 상태 필터 */}
            <div className="mb-3 flex flex-wrap gap-2">
              {filterButtons.map(({ name, type }) => (
                <TableFilterButton
                  key={type}
                  isActive={selectedSttCd === type}
                  onClick={() => handleFilterClick(type)}
                >
                  {name}
                </TableFilterButton>
              ))}
            </div>

            {/* 검색 필터 */}
            <div className="mb-4 flex flex-wrap items-center gap-2">
              <div className="w-36">
                <Controller
                  control={control}
                  name="plcCd"
                  render={({ field: { onChange, value } }) => (
                    <Dropdown
                      category="plcCd"
                      options={placeList}
                      disabled={placeList.length === 0}
                      onChangeOption={onChange}
                      selectedOption={value.name}
                    />
                  )}
                />
              </div>
              <div className="w-36">
                <Controller
                  name="fromDate"
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <DatePickerButton date={value} onChange={onChange} />
                  )}
                />
              </div>
              <div className="w-36">
                <Controller
                  name="toDate"
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <DatePickerButton date={value} onChange={onChange} />
                  )}
                />
              </div>
              <Button variant="outlined" onClick={handleSearch}>
                조회
              </Button>
            </div>
          </>
        )}

        {/* 테이블 */}
        <div className="min-h-[320px]">
          {activeTab === "place" ? (
            isPlaceLoading ? (
              <Spinner />
            ) : (
              <Table header={header} body={reservationBody} />
            )
          ) : isVehicleLoading ? (
            <Spinner />
          ) : (
            <Table header={vehicleHeader} body={vehicleBody} />
          )}
        </div>

        {activeTab === "place" && pageCount > 0 && (
          <div className="flex justify-center pt-4">
            <Pagination count={pageCount} page={page} onChange={setPage} />
          </div>
        )}
      </div>
      {confirmModal.isOpen && (
        <ConfirmModal
          title="예약 취소"
          onConfirm={onConfirm}
          onClose={confirmModal.onClose}
        >
          해당 예약을 취소하려면 확인 버튼을 클릭하세요
        </ConfirmModal>
      )}
      {alertModal.isOpen && (
        <AlertModal onClose={alertModal.onClose}>{alertMessage}</AlertModal>
      )}
    </MyPageWrapper>
  );
};

export default MyReservation;
